import { useMemo } from 'react';
import AutoSuggestWidget from 'react-autosuggest';
import pluralize from 'pluralize';

import { AIDictionaryYearRange } from 'global/constants';
import { theme as defaultTheme } from 'approot/shared/autosuggest/autosuggest.config';
import { ENTER_KEY } from 'lib/strings/strings';
import { useLoggingQuery } from 'lib/apollo/apollo-hooks';
import type {
  FindInDictionaryBookQuery,
  FindInDictionaryBookQueryVariables,
} from '../../../../dictionary/dictionary.graphql.generated';
import { FIND_IN_DICTIONARY_BOOK } from 'approot/dictionary/dictionary.graphql';
import {
  SettingsGroup,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import { QuotStyled } from 'approot/shared/styles/typography.style';
import {
  DictionaryDefinition,
  SOURCE_WORDS_API,
} from './vocabulary-setting.constants';
import { PrimaryButton } from 'approot/shared/styles/buttons';
import { VocabularyApprovedTooltip } from './vocabulary-setting-shared';
import * as Styled from './vocabulary-search.style';
import { BadgeSmallStyled } from 'approot/shared/badge/badge.style';
import { Box } from 'approot/shared/design-system/box/box.style';
import type { Pluck } from 'lib/gql/pluck';

type Props = {
  fieldName: string;
  term?: string;
  vocabularyIds: number[];
  yearGroup: AIDictionaryYearRange;
  contentLabel: string;
  setTerm: (term: string) => void;
  addDefinitionToContent: (id: number) => void;
  editUnapprovedDefinition: (data: DictionaryDefinition) => void;
  askAIForRawDefinition: () => void;
};

const VocabularySearch = ({
  fieldName,
  term,
  vocabularyIds,
  yearGroup,
  contentLabel,
  setTerm,
  addDefinitionToContent,
  editUnapprovedDefinition,
  askAIForRawDefinition,
}: Props) => {
  const { data, loading } = useLoggingQuery<
    FindInDictionaryBookQuery,
    FindInDictionaryBookQueryVariables
  >(FIND_IN_DICTIONARY_BOOK, {
    fetchPolicy: 'network-only',
    variables: {
      input: {
        word: term!,
      },
    },
    skip: !term,
  });

  const suggestions = useMemo(
    () => data?.findInDictionaryBook || [],
    [data?.findInDictionaryBook]
  );
  const filteredSuggestions = useMemo(
    () => suggestions.filter(s => !vocabularyIds.find(id => id === s.id)),
    [vocabularyIds, suggestions]
  );

  return (
    <SettingsGroup>
      <Styled.VocabularySearchContainer $itemClassName="item">
        <SettingsLabel marginBottom={4}>Vocabulary Search</SettingsLabel>
        <Styled.VocabularySearchContainerField data-selector="search-container">
          <AutoSuggestWidget<
            Pluck<FindInDictionaryBookQuery, 'findInDictionaryBook'>,
            Pluck<FindInDictionaryBookQuery, 'findInDictionaryBook'>
          >
            getSuggestionValue={suggestion => suggestion.word}
            inputProps={{
              id: fieldName,
              type: 'search',
              name: fieldName,
              autoComplete: 'off',
              onChange: (_, params) => {
                if (params.method === 'type') {
                  setTerm(params.newValue);
                  if (!params.newValue || params.newValue.trim() === '') {
                    setTerm('');
                  }
                }
              },
              onKeyDown: event => {
                if (event.key === ENTER_KEY) {
                  event.preventDefault();
                }
              },
              value: term || '',
              placeholder: 'Search for a word',
            }}
            onSuggestionsFetchRequested={event => setTerm(event.value)}
            onSuggestionsClearRequested={() => {}}
            onSuggestionSelected={(_, data) => {
              setTerm(data.suggestion.word);
              if (data.suggestion.approved) {
                addDefinitionToContent(data.suggestion.id);
              } else {
                editUnapprovedDefinition({
                  ...data.suggestion,
                  aiDefinitions: undefined,
                  source: SOURCE_WORDS_API,
                });
              }
            }}
            renderSuggestionsContainer={s => {
              return term && term.length > 0 ? (
                <div {...s.containerProps} data-selector="suggestion-lists">
                  {loading ? (
                    <Styled.VocabularySearchHelp>
                      Searching...
                    </Styled.VocabularySearchHelp>
                  ) : (
                    <>
                      {s.children}
                      {s.children && filteredSuggestions.length > 0 && (
                        <Styled.VocabularyStickySearchButton>
                          <PrimaryButton
                            type="button"
                            onMouseDown={askAIForRawDefinition}
                            data-selector="btn-find-more-definitions"
                          >
                            Find More Definitions Using AI ✨
                          </PrimaryButton>
                        </Styled.VocabularyStickySearchButton>
                      )}
                    </>
                  )}
                  {!loading && filteredSuggestions.length === 0 && (
                    <>
                      {suggestions.length > 0 ? (
                        <Styled.VocabularySearchHelp>
                          <Styled.VocabularyNotPresent>
                            <QuotStyled>{term}</QuotStyled> is already added in{' '}
                            {contentLabel}
                          </Styled.VocabularyNotPresent>
                          <PrimaryButton
                            type="button"
                            onClick={askAIForRawDefinition}
                          >
                            Find Definitions Using AI ✨
                          </PrimaryButton>
                        </Styled.VocabularySearchHelp>
                      ) : (
                        <>
                          <Styled.VocabularySearchHelp>
                            <Styled.VocabularyNotPresent data-selector="vocab-not-present">
                              <QuotStyled>{term}</QuotStyled> isn't in our
                              dictionary yet
                            </Styled.VocabularyNotPresent>

                            <PrimaryButton
                              type="button"
                              onClick={askAIForRawDefinition}
                              data-selector="btn-find-definitions"
                            >
                              Find Definitions Using AI ✨
                            </PrimaryButton>
                          </Styled.VocabularySearchHelp>
                        </>
                      )}
                    </>
                  )}
                </div>
              ) : null;
            }}
            renderSuggestion={suggestion => {
              const aiDefinition = suggestion.aiDefinitions?.definitions.find(
                def => def.year === yearGroup
              );
              const count = suggestion.data?.count;
              return (
                <div key={suggestion.id} data-selector="suggestion">
                  <Styled.VocabularySearchResult data-selector="vocab-search-results">
                    {!!count && (
                      <Box $marginBottom={4}>
                        <BadgeSmallStyled>
                          Used in {pluralize('lesson', count, true)}
                        </BadgeSmallStyled>
                      </Box>
                    )}
                    <strong>{suggestion.word}</strong>{' '}
                    <i>({suggestion.lexical})</i>
                    {suggestion.approved && <VocabularyApprovedTooltip />}
                  </Styled.VocabularySearchResult>
                  <Styled.VocabularySearchResult>
                    {aiDefinition
                      ? aiDefinition.definition
                      : suggestion.sourceDefinition}
                  </Styled.VocabularySearchResult>
                </div>
              );
            }}
            suggestions={filteredSuggestions}
            theme={defaultTheme}
            focusInputOnSuggestionClick={false}
          />
        </Styled.VocabularySearchContainerField>
      </Styled.VocabularySearchContainer>
    </SettingsGroup>
  );
};

export default VocabularySearch;
